/**
 * Coupons & Promos — shared visual primitives. Verbatim port of the prototype's
 * DTypeGlyph / DStatusPill / DTypePill / DiscountPreview.
 */
import { Card, Icon, Pill, T } from "@v0y4ge/jrni-ui";
import { discountValueLabel, type Discount } from "./promos-data";
import { useT } from "../../lib/i18n";

export function DTypeGlyph({ type, size = 15, color }: { type: string; size?: number; color?: string }) {
  const c = color || "currentColor";
  return type === "promo" ? (
    <svg viewBox="0 0 20 20" width={size} height={size} fill="none" stroke={c} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M14 6 6 14" /><circle cx="7" cy="7" r="1.6" /><circle cx="13" cy="13" r="1.6" /><path d="M3 8V5a2 2 0 0 1 2-2h3" />
    </svg>
  ) : (
    <svg viewBox="0 0 20 20" width={size} height={size} fill="none" stroke={c} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M3 7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2 1.6 1.6 0 0 0 0 6 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 1.6 1.6 0 0 0 0-6z" /><path d="M11 7v6" strokeDasharray="1.5 2" />
    </svg>
  );
}

export function DStatusPill({ status }: { status: string }) {
  const t = useT();
  return status === "approved" ? <Pill tone="success" dot>{t("promos.shared.approved", "Approved")}</Pill> : <Pill tone="warn" dot>{t("common.status.draft", "Draft")}</Pill>;
}

export function DTypePill({ type }: { type: string }) {
  const t = useT();
  return <Pill tone={type === "promo" ? "primary" : "neutral"}>{type === "promo" ? t("promos.shared.text", "Promo") : t("promos.shared.text2", "Coupon")}</Pill>;
}

/** A small checkout preview of a discount, applied to a sample $120 subtotal. */
export function DiscountPreview({ d }: { d: Discount }) {
  const t = useT();
  const subtotal = 120;
  const off = d.kind === "percent" ? Math.round((subtotal * d.value) / 100) : Math.min(subtotal, d.value);
  const total = Math.max(0, subtotal - off);
  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{t("promos.shared.checkoutPreview", "Checkout preview")}</div>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {d.type === "coupon" ? (
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, height: 40, border: `1.5px dashed ${T.p2}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 8, padding: "0 12px", background: T.p4 }}>
              <Icon.check size={14} style={{ color: T.green1 }} />
              <span style={{ fontFamily: T.fontMono, fontWeight: 600, color: T.n1 }}>{d.code || "CODE"}</span>
              <span style={{ marginLeft: "auto", fontSize: 12.5, color: T.p1, fontWeight: 600 }}>{discountValueLabel(d)} {t("promos.shared.applied", "applied")}</span>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8 }}>
            <DTypeGlyph type="promo" size={15} color={T.p1} />
            <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("promos.shared.automatic", "Automatic:")}{" "}{d.name || t("promos.shared.text", "Promo")}</span>
            <span style={{ marginLeft: "auto", fontSize: 12.5, color: T.p1, fontWeight: 600 }}>−{discountValueLabel(d)}</span>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
          <div style={{ display: "flex", justifyContent: "space-between", color: T.n2 }}><span>{t("promos.shared.subtotal", "Subtotal")}</span><span>${subtotal.toFixed(2)}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", color: T.green1, fontWeight: 600 }}><span>{t("promos.shared.discount", "Discount")}</span><span>−${off.toFixed(2)}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", color: T.n1, fontWeight: 700, fontSize: 15, paddingTop: 6, borderTop: `1px solid ${T.border}` }}><span>{t("promos.shared.total", "Total")}</span><span>${total.toFixed(2)}</span></div>
        </div>
      </div>
    </Card>
  );
}
